import React, { useCallback, useState, useEffect, useRef } from 'react';
import { DataGrid, Column, FilterRow, Scrolling, SearchPanel, Lookup, Editing, Selection } from 'devextreme-react/data-grid';
import { TextBox } from 'devextreme-react';
import { RowInsertingEvent, RowUpdatingEvent, RowRemovingEvent } from 'devextreme/ui/data_grid';
import DropDownBox from 'devextreme-react/drop-down-box';

import { formatMessage } from 'devextreme/localization';

import GlobalLoader from '../loader/GlobalLoader';

import ProcessingResult from '../../framework/ProcessingResult';

import { UserRolesHelper } from '../../logics/usermodule/UserRolesHelper';

import UserApi from '../../api/UserApi';
import RoleGroupApi, { RoleGroupFilterMode } from '../../api/RoleGroupApi';

import RoleGroupDto from '../../classes/dtos/rolegroupmodule/RoleGroupDto';
import UserListDto from '../../classes/dtos/accountmodule/UserListDto';

import { useAuth } from '../../contexts/auth';


interface RoleGroupGridProps {
}

const RoleGroupGrid: React.FC<RoleGroupGridProps> = () => {

    const [roleGroups, setRoleGroups] = useState<Array<RoleGroupDto> | null>(null);
    const [users, setUsers] = useState<Array<UserListDto> | null>(null);

    const [currentUserId] = useState<number>(useAuth().user?.UserId ?? 0);

    const userRoles = UserRolesHelper.getUserRoles();

    const refresh = useCallback(() => {
        if (!roleGroups) {
            RoleGroupApi.GetRoleGroupAsync(RoleGroupFilterMode.Workspace).then((allRoleGroups: ProcessingResult<RoleGroupDto[]>) => {
                if (allRoleGroups.IsFailed()) {
                    // TODO: Handle failure case (e.g., logout)
                } else {

                    setRoleGroups(allRoleGroups.Result);
                }
            });
        }
    }, [roleGroups, userRoles]);

    // UseEffect to refresh when component mounts
    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        if (users === null) {
            UserApi.GetUserListAsync(false)
                .then(list => {
                    setUsers(list.Result);
                });
        }
    }, [users]);

    function insertRow(e: RowInsertingEvent<RoleGroupDto, any>) {

        RoleGroupApi.CreateRoleGroupAsync(e.data.Name, e.data.UserRole, e.data.UserIds)
            .then((e) => {
                if (e.IsFailed()) {
                    alert("ERROR");
                }
                setRoleGroups(null);
            });
    }

    function updateRow(e: RowUpdatingEvent<RoleGroupDto, any>) {

        let id = e.newData.Id ?? e.oldData.Id;
        let name = e.newData.Name ?? e.oldData.Name;
        let userRole = e.newData.UserRole ?? e.oldData.UserRole;
        let userIds = e.newData.UserIds ?? e.oldData.UserIds;

        RoleGroupApi.UpdateRoleGroupAsync(id, name, userRole, userIds)
            .then((e) => {
                if (e.IsFailed()) {
                    alert("ERROR");
                }
                setRoleGroups(null);
            });
    }

    function removeRow(e: RowRemovingEvent<RoleGroupDto, any>) {

        RoleGroupApi.DeleteRoleGroupAsync(e.data.Id)
            .then((e) => {
                if (e.IsFailed()) {
                    alert("ERROR");
                }
                setRoleGroups(null);
            });
    }

    if (!roleGroups) {
        return <GlobalLoader />
    }

    // Composant DropDownBox avec DataGrid interne
    const GridInDropDown = (cellInfo: any) => {
        const [selectedKeys, setSelectedKeys] = useState(cellInfo.value || []);
        const [isOpened, setIsOpened] = useState(false);

        const onSelectionChanged = (e: any) => {
            setSelectedKeys(e.selectedRowKeys);
            cellInfo.setValue(e.selectedRowKeys);
        };

        return (

            <DropDownBox
                value={selectedKeys}
                opened={isOpened}
                valueExpr="Id"
                placeholder={formatMessage("RoleGroupGrid_SelectAUser")}
                showClearButton={false}
                dataSource={users}
                onValueChanged={(e) => {
                    setSelectedKeys(e.value || []);
                    cellInfo.setValue(e.value || []);
                }}
                onOpenedChange={(opened) => setIsOpened(opened)}
                fieldRender={() => (
                    <TextBox>
                        <div style={{ marginLeft: '16px', flexDirection: 'row', display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 2, lineHeight: '20px', cursor: 'pointer' }}>
                            {(users || [])
                                .filter(cac => (cellInfo.value ?? []).includes(cac.Id))
                                .map((cac, idx, arr) => (
                                    <span key={cac.Id} style={{ background: '#b33d3d', borderRadius: '14px', paddingLeft: '16px', paddingRight: '16px', marginRight: idx < arr.length - 1 ? '4px' : '0', display: 'inline-block', whiteSpace: 'nowrap', height: '20px' }}>
                                        {cac.Label}
                                    </span>
                                ))
                            }
                            {(!cellInfo.value || cellInfo.value.length === 0) && (
                                <span>
                                    {formatMessage("RoleGroupGrid_NoUsers")}
                                </span>
                            )}
                        </div>
                    </TextBox>
                )}
                contentRender={() => (
                    <DataGrid
                        dataSource={users}
                        selectedRowKeys={selectedKeys}
                        onSelectionChanged={onSelectionChanged}
                        hoverStateEnabled={true}
                        height={300}
                        keyExpr="Id"
                        showBorders={true}
                    >
                        <Selection mode="multiple" showCheckBoxesMode="always" />
                        <Column dataField="FirstName" caption="FirstName" minWidth={200} />
                        <Column dataField="LastName" caption="LastName" minWidth={200} />
                        <Column dataField="Email" caption="E-mail" dataType="date" minWidth={200} />
                    </DataGrid>
                )}
            />
        );
    };

    const userTagsCell = (cellData: any) => {
        const userIds = cellData.value || [];

        const selectedItems = (users ?? []).filter(c =>
            userIds.includes(c.Id)
        );

        return (
            <div style={{ flexDirection: 'row', display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 2, lineHeight: '20px' }}>
                {selectedItems.length === 0 ? (
                    <span>{formatMessage("RoleGroupGrid_NoUsers")}</span>
                ) : (
                    selectedItems.map((cac, idx, arr) => (
                        <span key={cac.Id} style={{ background: '#b33d3d', borderRadius: '14px', paddingLeft: '16px', paddingRight: '16px', marginRight: idx < arr.length - 1 ? '4px' : '0', display: 'inline-block', whiteSpace: 'nowrap', height: '20px' }}>
                            {cac.Label}
                        </span>
                    ))
                )}
            </div>
        );
    };

    return (
        <div className={"block-card"}>
            <div className={"block-card__header"}>
                <h2>{"All your groups"}</h2>
            </div>
            <DataGrid
                id="dataGrid"
                dataSource={roleGroups}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={false}
                hoverStateEnabled={true}
                height='calc(100vh - 400px)'
                onRowInserting={insertRow}
                onRowUpdating={updateRow}
                onRowRemoving={removeRow}
            >
                <Scrolling mode="infinite" />
                <FilterRow visible={false} />
                <SearchPanel visible={false} />
                <Editing
                    mode="row"
                    allowUpdating={true}
                    allowAdding={true}
                    allowDeleting={true}
                />
                <Column caption="NAME" dataField="Name" dataType="string" allowSorting={true} allowEditing={true} width='30%' />
                <Column caption="ROLE" dataField="UserRole" dataType="number" allowSorting={true} allowEditing={true} width='200px'>
                    <Lookup
                        displayExpr="Name"
                        valueExpr="Id"
                        dataSource={userRoles}
                    />
                </Column>
                <Column
                    dataField="UserIds"
                    caption={formatMessage("RoleGroupGrid_Users")}
                    editCellRender={GridInDropDown}
                    cellRender={userTagsCell}
                    allowSorting={false}
                    calculateDisplayValue={(rowData) => {
                        if (!rowData.UserIds || rowData.UserIds.length === 0) {
                            return formatMessage("RoleGroupGrid_NoUsers");
                        }
                        return rowData.UserIds
                            .map((id: number) => {
                                const cac = users!.find(c => c.Id === id);
                                return cac ? cac.Label : '';
                            })
                            .filter(Boolean)
                            .join(', ');
                    }}
                    width='70%'
                />
            </DataGrid>
        </div>
    );
};

export default RoleGroupGrid;
